import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import type { SchemaFieldType } from '@erp/shared-types';

const JSONB_FIELD_TYPES: ReadonlySet<SchemaFieldType> = new Set([
  'json',
  'range',
  'object',
  'array',
  'unit',
  'geospatial',
]);

const NUMBER_FIELD_TYPES: ReadonlySet<SchemaFieldType> = new Set([
  'integer',
  'float',
  'currency',
  'numeric',
]);

const TEMPORAL_FIELD_TYPES: ReadonlySet<SchemaFieldType> = new Set([
  'date',
  'time',
  'datetime',
]);

const BOOLEAN_OPTIONS = ['true', 'false'] as const;

/**
 * Whether the (hasDefault, value) pair is a valid default for the type. Numeric
 * and temporal types reject blank/invalid values (e.g. a datetime cannot
 * default to an empty string); string-like types allow the empty string. When
 * the default is disabled, the value is irrelevant and always valid.
 */
export function isDefaultValueValid(
  fieldType: SchemaFieldType,
  hasDefault: boolean,
  value: unknown,
): boolean {
  if (!hasDefault) return true;
  if (NUMBER_FIELD_TYPES.has(fieldType)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false;
    return fieldType === 'integer' ? Number.isInteger(value) : true;
  }
  if (TEMPORAL_FIELD_TYPES.has(fieldType)) {
    return typeof value === 'string' && value.trim() !== '';
  }
  return true;
}

/**
 * Coerces a raw input string into the JSON-serializable value appropriate for
 * `fieldType`, so it round-trips cleanly through the `defaultValue` JSONB column.
 * Empty input is treated as "no default" (null). Invalid numbers / JSON are
 * kept as the raw string so the backend (or link validation) flags them rather
 * than silently dropping the value.
 */
export function coerceDefaultValue(
  fieldType: SchemaFieldType,
  raw: string,
): unknown {
  const trimmed = raw.trim();
  if (NUMBER_FIELD_TYPES.has(fieldType)) {
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : raw;
  }
  if (JSONB_FIELD_TYPES.has(fieldType)) {
    if (trimmed === '') return null;
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return raw;
    }
  }
  if (TEMPORAL_FIELD_TYPES.has(fieldType)) {
    // There is no meaningful empty date/time/datetime default — a blank input
    // means "no value entered" (null), never the empty string.
    return trimmed === '' ? null : raw;
  }
  // Only genuine string types treat the empty string as a real default
  // (e.g. a text column defaulting to '').
  return raw;
}

/**
 * The initial value to seed when a field's default is first enabled. Only
 * genuine string types have a usable empty default (''); numeric, temporal,
 * and JSON types start as null so the user must supply a real value.
 */
export function initialDefaultForType(fieldType: SchemaFieldType): unknown {
  if (fieldType === 'boolean') return false;
  if (
    NUMBER_FIELD_TYPES.has(fieldType) ||
    TEMPORAL_FIELD_TYPES.has(fieldType) ||
    JSONB_FIELD_TYPES.has(fieldType)
  ) {
    return null;
  }
  return '';
}

/**
 * Renders a stored default value as a short, display-friendly string. An empty
 * string is the empty-string default and renders blank; a value of `""`
 * (two double-quote characters) renders literally as `""`.
 */
export function formatDefaultValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

interface DefaultValueInputProps {
  fieldType: SchemaFieldType;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /** Tighter sizing for dense rows (e.g. the quick-create dialog). */
  compact?: boolean;
  /** Overrides the input placeholder (e.g. a "no default" hint when disabled). */
  placeholder?: string;
}

/**
 * Type-aware editor for a schema field's default value. Remount it (e.g.
 * `key={fieldType}`) when the field type changes so the internal text state
 * re-initializes from the incoming value.
 */
export function DefaultValueInput({
  fieldType,
  value,
  onChange,
  disabled,
  compact,
  placeholder,
}: DefaultValueInputProps) {
  const gt = useGT();
  const compactClass = compact ? 'h-8 text-xs' : undefined;

  // String / number / JSON inputs keep a local text buffer so typing stays
  // smooth (no re-stringify per keystroke). Declared before any early return so
  // the hook order stays stable across renders.
  const [text, setText] = useState(() => formatDefaultValue(value));

  function emit(raw: string) {
    setText(raw);
    onChange(coerceDefaultValue(fieldType, raw));
  }

  if (fieldType === 'boolean') {
    const current = value === true ? 'true' : value === false ? 'false' : '';
    return (
      <Select
        value={current || undefined}
        onValueChange={(v) => onChange(v === 'true')}
        disabled={disabled}
      >
        <SelectTrigger className={compactClass}>
          <SelectValue placeholder={placeholder ?? gt('Select…')} />
        </SelectTrigger>
        <SelectContent>
          {BOOLEAN_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (JSONB_FIELD_TYPES.has(fieldType)) {
    const isInvalid =
      text.trim() !== '' &&
      (() => {
        try {
          JSON.parse(text);
          return false;
        } catch {
          return true;
        }
      })();
    return (
      <div className="space-y-1">
        <Textarea
          value={text}
          onChange={(e) => emit(e.target.value)}
          disabled={disabled}
          rows={2}
          className="font-mono text-xs"
          placeholder={placeholder ?? gt('JSON value')}
        />
        {isInvalid && (
          <p className="text-xs text-destructive">
            <T context="hint when the default value JSON cannot be parsed">
              Invalid JSON
            </T>
          </p>
        )}
      </div>
    );
  }

  return (
    <Input
      type={NUMBER_FIELD_TYPES.has(fieldType) ? 'number' : 'text'}
      value={text}
      onChange={(e) => emit(e.target.value)}
      disabled={disabled}
      className={compactClass}
      placeholder={placeholder ?? gt('Default value')}
    />
  );
}
